import { jsPDF } from "jspdf";
import intl from "react-intl-universal";
import { ClassificatorResults } from "../../classification/Classificator";
import { constructList, constructSeizureType, constructSemiology } from "../../classification/Seizures";

type FeatureEntry = [string, any];

const sectionLabels: Record<string, string> = {
    age: "Current age",
    ageUnit: "Age unit",
    ageAtOnset: "Age at onset",
    ageAtOnsetUnit: "Age-at-onset unit",
    gender: "Gender",
    development: "Development",
    neuroexam: "Neurological examination",
    eeg: "EEG",
    history: "History",
    imaging: "Imaging",
    tests: "Further tests",
    alertCriteria: "Selected alert criteria",
    swFreq: "Spike-wave frequency",
    devAge: "Developmental age",
    devAgeUnit: "Developmental age unit",
    devAgeTest: "Developmental age test",
    devFreeText: "Development notes",
    neuroexamFreeText: "Neurological examination notes",
    neuroexamCourseFreeText: "Neurological examination course notes",
    eeg_other_sleep_findings: "Other sleep EEG findings",
    personalFreeText: "Personal history notes",
    familyFreeText: "Family history notes",
    treatmentFreeText: "Treatment notes",
    otherFreeText: "Other history notes",
    otherImaging: "Other imaging findings",
    genTestsFree: "Genetic test notes",
    bloodTestsFree: "Blood test notes"
};

const freeTextKeys = new Set([
    "devFreeText", "neuroexamFreeText", "neuroexamCourseFreeText", "eeg_other_sleep_findings",
    "personalFreeText", "familyFreeText", "treatmentFreeText", "otherFreeText", "otherImaging",
    "genTestsFree", "bloodTestsFree"
]);

function displayFeature(value: string): string {
    if (typeof value !== "string") {
        return String(value ?? "");
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed === "*") {
        return "";
    }

    if (/^\d+(\.\d+)?$/.test(trimmed)) {
        return trimmed;
    }

    if (["years", "months", "days", "male", "female", "unknown"].includes(trimmed)) {
        return trimmed;
    }

    const translated = intl.get(trimmed);
    if (typeof translated === "string") {
        return translated === trimmed ? trimmed.replace(/_/g, " ") : translated;
    }

    return trimmed.replace(/_/g, " ");
}

function formatFeatureValue(key: string, value: any): string {
    if (value === undefined || value === null || value === "") return "";
    if (Array.isArray(value)) {
        return value.filter(item => item !== "" && item !== "*").map(item => displayFeature(String(item))).join(", ");
    }
    if (typeof value === "string") {
        return freeTextKeys.has(key) ? value : displayFeature(value);
    }
    return String(value);
}

function formatSeizures(seizures: any[]): string[] {
    return seizures.map((seizure, index) => {
        const details = [
            constructSeizureType(seizure.mainType, seizure.awareness),
            seizure.onset && seizure.onset !== "*" ? `Onset: ${displayFeature(seizure.onset)}` : "",
            seizure.details?.length ? `Semiology: ${constructSemiology(seizure.details)}` : "",
            seizure.timepoint && seizure.timepoint !== "*" ? `Wake/sleep: ${displayFeature(seizure.timepoint)}` : "",
            seizure.duration?.length ? `Duration: ${constructList(seizure.duration)}` : "",
            seizure.triggers?.length ? `Triggers: ${constructList(seizure.triggers)}` : "",
            seizure.frequency?.length ? `Frequency: ${constructList(seizure.frequency)}` : "",
            seizure.other?.length ? `Other: ${constructList(seizure.other)}` : ""
        ].filter(Boolean);
        return `Seizure ${index + 1}: ${details.join("; ")}`;
    });
}

function collectFeatureLines(features: Map<string, any>): string[] {
    const lines: string[] = [];
    const handled = new Set<string>(["seizures"]);
    const groups: Array<[string, string[]]> = [
        ["Development", ["development", "devAge", "devAgeUnit", "devAgeTest", "devFreeText"]],
        ["Neurological examination", ["neuroexam", "neuroexamFreeText", "neuroexamCourseFreeText"]],
        ["EEG", ["eeg", "swFreq", "eeg_other_sleep_findings"]],
        ["History", ["history", "personalFreeText", "familyFreeText", "treatmentFreeText", "otherFreeText"]],
        ["Imaging", ["imaging", "otherImaging"]],
        ["Further tests", ["tests", "genTestsFree", "bloodTestsFree"]],
        ["Alert criteria", ["alertCriteria"]]
    ];

    const ageText = features.get("age");
    const onsetText = features.get("ageAtOnset");
    const ageUnit = features.get("ageUnit") ?? "years";
    const onsetUnit = features.get("ageAtOnsetUnit") ?? "years";

    if (ageText || onsetText) {
        lines.push("Patient profile");
        if (ageText) {
            lines.push(`Current age: ${formatFeatureValue("age", ageText)} ${formatFeatureValue("ageUnit", ageUnit)}`.trim());
        }
        if (onsetText) {
            lines.push(`Seizure onset age: ${formatFeatureValue("ageAtOnset", onsetText)} ${formatFeatureValue("ageAtOnsetUnit", onsetUnit)}`.trim());
        }
    }
    lines.push(`Gender: ${formatFeatureValue("gender", features.get("gender"))}`.trim());

    handled.add("age");
    handled.add("ageAtOnset");
    handled.add("ageUnit");
    handled.add("ageAtOnsetUnit");
    handled.add("gender");

    for (const [heading, keys] of groups) {
        const entries = keys.map(key => {
            if (key === "age" || key === "ageAtOnset" || key === "ageUnit" || key === "ageAtOnsetUnit") {
                return "";
            }
            handled.add(key);
            const formatted = formatFeatureValue(key, features.get(key));
            return formatted ? `${sectionLabels[key] || key}: ${formatted}` : "";
        }).filter(Boolean);
        if (entries.length) lines.push(heading, ...entries);
    }

    const seizures = features.get("seizures");
    if (Array.isArray(seizures) && seizures.length) {
        lines.push("Seizures", ...formatSeizures(seizures));
    }

    const remaining: FeatureEntry[] = Array.from(features.entries()).filter(([key]) => !handled.has(key));
    if (remaining.length) {
        lines.push("Other selected features");
        remaining.forEach(([key, value]) => {
            const formatted = formatFeatureValue(key, value);
            if (formatted) lines.push(`${sectionLabels[key] || key.replace(/([A-Z])/g, " $1")}: ${formatted}`);
        });
    }

    return lines.length ? lines : ["No feature information entered."];
}

function classificationLines(classifications: ClassificatorResults): string[] {
    const categories: Array<[string, any[] | undefined]> = [
        ["Likely", classifications.likely],
        ["Likely with alerts", classifications.likelyWithAlerts],
        ["Uncertain", classifications.unclear],
        ["Unresolved (essential investigation missing)", classifications.unresolved],
        ["Excluded", classifications.excluded]
    ];

    return categories.flatMap(([label, syndromes]) => [
        label,
        ...(syndromes?.length ? syndromes.map(syndrome => `- ${displayFeature(syndrome.getName())}`) : ["None"])
    ]);
}

export function createCaseSummary(features: Map<string, any>, classifications: ClassificatorResults): string[] {
    return [
        "Selected features",
        ...collectFeatureLines(features),
        "Syndrome classification",
        ...classificationLines(classifications)
    ];
}

export function downloadCaseSummary(features: Map<string, any>, classifications: ClassificatorResults, caseId: string): void {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 18;
    const lineHeight = 6;
    let y = 20;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text("Case summary", margin, y);
    y += 10;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    if (caseId) {
        pdf.text(`Case ID: ${caseId}`, margin, y);
        y += lineHeight;
    }
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, y);
    y += 10;

    const headings = new Set([
        "Selected features", "Syndrome classification", "Patient profile", "Development",
        "Neurological examination", "EEG", "History", "Imaging", "Further tests",
        "Alert criteria", "Seizures", "Other selected features", "Likely", "Likely with alerts",
        "Uncertain", "Unresolved (essential investigation missing)", "Excluded"
    ]);
    for (const line of createCaseSummary(features, classifications)) {
        const isHeading = headings.has(line);
        const wrapped = pdf.splitTextToSize(line, pageWidth - margin * 2);
        const requiredHeight = wrapped.length * lineHeight + (isHeading ? 2 : 0);
        if (y + requiredHeight > pageHeight - margin) {
            pdf.addPage();
            y = margin;
        }
        if (isHeading) {
            y += 2;
            pdf.setFont("helvetica", "bold");
        } else {
            pdf.setFont("helvetica", "normal");
        }
        pdf.text(wrapped, margin, y);
        y += wrapped.length * lineHeight;
    }

    pdf.save(`case-summary${caseId ? `-${caseId}` : ""}.pdf`);
}